/**
 * navRegistry - Single source of truth for the V2 navigation + action registry.
 *
 * Refactored FROM AppSidebar.jsx's `navigationGroups` / `quickActions` data
 * (Institutional Console redesign, issue #232 — see
 * .claude/docs/tasks/ui-redesign-console/sessions/2026-07-10_system-architect_gap-analysis.md
 * decision CC4). URLs are reused VERBATIM from the old sidebar — do not invent
 * new paths here; the flat/param route table in App.js is unchanged.
 *
 * Consumed by:
 * - TopNav.jsx: top-level items (DESIGN-SPEC.md §3 top bar)
 * - PageToolbar.jsx: per-section sub-tabs (DESIGN-SPEC.md §3 page toolbar)
 * - CommandPalette.jsx: full navigation entries + quick actions (⌘K, CC2)
 */

/**
 * Top-level TopNav items (DESIGN-SPEC.md §3):
 * `Logo · Overview · Holdings · Activity · Cash flow · Analytics · Insights · ...`
 *
 * `matchPrefixes` decides when this top-level item is "active" for a given
 * pathname (accent-tint bg + accent-text per §3). `defaultUrl` is where the
 * nav link itself points (first sub-tab / flat page).
 */
export const topNavItems = [
  { id: 'overview', label: 'Overview', defaultUrl: '/', matchPrefixes: ['/'], exact: true },
  {
    id: 'holdings',
    label: 'Holdings',
    defaultUrl: '/holdings/investments',
    matchPrefixes: ['/holdings'],
  },
  {
    id: 'activity',
    label: 'Activity',
    defaultUrl: '/transactions/activity',
    // Activity absorbs Contributions, Events, Transactions (per §3 IA mapping)
    matchPrefixes: ['/transactions', '/contributions', '/events'],
  },
  {
    id: 'cashflow',
    label: 'Cash flow',
    defaultUrl: '/cash-flow/income',
    matchPrefixes: ['/cash-flow'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    defaultUrl: '/analytics/performance',
    matchPrefixes: ['/analytics'],
  },
  {
    id: 'insights',
    label: 'Insights',
    defaultUrl: '/insights',
    matchPrefixes: ['/insights'],
    unread: 'insights', // 6px accent dot when insights unread count > 0
  },
];

/**
 * Determine which top-level nav id is active for a given pathname.
 * '/' only matches exactly (exact: true) so it doesn't swallow every route.
 */
export function getActiveTopNavId(pathname) {
  const match = topNavItems.find((item) => {
    if (item.exact) return pathname === item.defaultUrl;
    return item.matchPrefixes.some((prefix) => pathname.startsWith(prefix));
  });
  return match?.id ?? null;
}

/**
 * PageToolbar sub-tabs, keyed by the TopNav section id.
 * `param` = the useParams() key that drives the flat/param route (CC4);
 * `pathname` = used for flat pages that have no :type param (Contributions/Events).
 */
export const subTabGroups = {
  holdings: {
    param: 'type',
    basePath: '/holdings',
    tabs: [
      { value: 'investments', label: 'Investments', url: '/holdings/investments' },
      { value: 'cash', label: 'Cash', url: '/holdings/cash' },
      { value: 'real-estate', label: 'Real estate', url: '/holdings/real-estate' },
      { value: 'vehicles', label: 'Vehicles', url: '/holdings/vehicles' },
      { value: 'liabilities', label: 'Liabilities', url: '/holdings/liabilities' },
    ],
  },
  activity: {
    // Flat pages (Contributions, Events) + param page (Transactions) share this
    // toolbar group; active tab is derived from pathname, see getActiveSubTab().
    //
    // DEVIATION from DESIGN-SPEC.md §3, which lists Activity tabs as only
    // "Feed · Realised gains · Dividends · Contributions · Events(3)". The
    // old AppSidebar also exposed /transactions/withdrawals as a 4th
    // Transactions sub-item, and Withdrawals.jsx is a fully implemented,
    // reachable page (App.js /transactions/:type route) — dropping it would
    // violate the "no functionality lost" review gate. Added as an extra tab
    // rather than silently orphaning the route (no back-link would exist to
    // reach it otherwise). Flag for spec update / reviewer awareness.
    tabs: [
      { value: 'activity', label: 'Feed', url: '/transactions/activity' },
      { value: 'realized-gains', label: 'Realised gains', url: '/transactions/realized-gains' },
      { value: 'dividends', label: 'Dividends', url: '/transactions/dividends' },
      { value: 'withdrawals', label: 'Withdrawals', url: '/transactions/withdrawals' },
      { value: 'contributions', label: 'Contributions', url: '/contributions' },
      { value: 'events', label: 'Events', url: '/events', unread: 'events' },
    ],
  },
  cashflow: {
    param: 'type',
    basePath: '/cash-flow',
    tabs: [
      { value: 'income', label: 'Income', url: '/cash-flow/income' },
      { value: 'expenses', label: 'Expenses', url: '/cash-flow/expenses' },
    ],
  },
  analytics: {
    param: 'type',
    basePath: '/analytics',
    tabs: [
      { value: 'insights', label: 'Insights', url: '/analytics/insights' },
      { value: 'performance', label: 'Performance', url: '/analytics/performance' },
      { value: 'what-if', label: 'What-if', url: '/analytics/what-if' },
    ],
  },
};

/**
 * Determine the active sub-tab value for a section, given the current pathname
 * and an optional :type param (per CC4: unit tests should only assert render
 * for a given route; navigation click-through belongs in E2E).
 */
export function getActiveSubTab(sectionId, pathname, typeParam) {
  const group = subTabGroups[sectionId];
  if (!group) return null;

  if (group.param && typeParam) {
    return group.tabs.find((t) => t.value === typeParam)?.value ?? null;
  }

  // Flat-page match (Contributions/Events) or pathname-based param sections
  const match = group.tabs.find((t) => pathname === t.url || pathname.startsWith(`${t.url}/`));
  return match?.value ?? null;
}

/**
 * Quick Actions — refactored from AppSidebar.jsx `quickActions`. These are
 * dispatched via useV2Actions().handleAction (V2ActionsContext), preserved
 * verbatim from the sidebar implementation.
 */
export const quickActions = [
  { id: 'add-holding', label: 'Add holding', action: 'add-holding', keywords: 'new create asset' },
  { id: 'take-snapshot', label: 'Create snapshot', action: 'take-snapshot', keywords: 'snapshot save' },
];

/**
 * Full flat navigation entry list for the command palette (⌘K) — every
 * screen/sub-tab reachable from the old sidebar, reused verbatim.
 */
export const paletteNavigationEntries = [
  { id: 'nav-overview', label: 'Overview', url: '/', keywords: 'dashboard home' },
  { id: 'nav-holdings-investments', label: 'Holdings – Investments', url: '/holdings/investments', keywords: 'stocks etf' },
  { id: 'nav-holdings-cash', label: 'Holdings – Cash', url: '/holdings/cash', keywords: 'bank account' },
  { id: 'nav-holdings-real-estate', label: 'Holdings – Real estate', url: '/holdings/real-estate', keywords: 'property' },
  { id: 'nav-holdings-vehicles', label: 'Holdings – Vehicles', url: '/holdings/vehicles', keywords: 'car equipment' },
  { id: 'nav-holdings-liabilities', label: 'Holdings – Liabilities', url: '/holdings/liabilities', keywords: 'debt loan' },
  { id: 'nav-activity-feed', label: 'Activity – Feed', url: '/transactions/activity', keywords: 'transactions' },
  { id: 'nav-activity-realized-gains', label: 'Activity – Realised gains', url: '/transactions/realized-gains', keywords: 'sold profit' },
  { id: 'nav-activity-dividends', label: 'Activity – Dividends', url: '/transactions/dividends', keywords: 'income' },
  { id: 'nav-activity-withdrawals', label: 'Activity – Withdrawals', url: '/transactions/withdrawals', keywords: 'cash out' },
  { id: 'nav-contributions', label: 'Contributions', url: '/contributions', keywords: 'deposit withdrawal' },
  { id: 'nav-events', label: 'Events', url: '/events', keywords: 'log activity notifications', unread: 'events' },
  { id: 'nav-cashflow-income', label: 'Cash flow – Income', url: '/cash-flow/income', keywords: 'recurring' },
  { id: 'nav-cashflow-expenses', label: 'Cash flow – Expenses', url: '/cash-flow/expenses', keywords: 'spending' },
  { id: 'nav-analytics-insights', label: 'Analytics – Insights', url: '/analytics/insights', keywords: 'summary' },
  { id: 'nav-analytics-performance', label: 'Analytics – Performance', url: '/analytics/performance', keywords: 'charts benchmark' },
  { id: 'nav-analytics-whatif', label: 'Analytics – What-if', url: '/analytics/what-if', keywords: 'simulate calculator' },
  { id: 'nav-insights', label: 'Insights', url: '/insights', keywords: 'daily digest', unread: 'insights' },
  { id: 'nav-snapshots', label: 'Snapshots', url: '/snapshots', keywords: 'history breadcrumb' },
  { id: 'nav-settings', label: 'Settings', url: '/settings', keywords: 'appearance theme account mcp' },
];

/**
 * Admin-only palette navigation entry (mirrors AppSidebar's conditional Admin
 * group). Appended by the caller when user.isAdmin is true.
 */
export const adminPaletteEntry = {
  id: 'nav-admin-metrics',
  label: 'System metrics',
  url: '/admin/metrics',
  keywords: 'admin health',
};
